(() => {
  'use strict';

  // Search a machine family by its actual product type, then load only parts
  // whose titles name a model shown in the machine results.  The catalogue
  // and compatible-brand tags are the source of truth for machine/part status.
  const FAMILIES = [
    {key:'generator', aliases:['generators','generator'], types:['Generators']},
    {key:'lawn-mower', aliases:['lawn mowers','lawn mower','walk behind mowers','push mowers'], types:['Lawn Mowers']},
    {key:'ride-on-mower', aliases:['ride on mowers','ride-on mowers','riding mowers','ride ons','zero turn mowers','zero turns'], types:['Ride-on Mowers']},
    {key:'robotic-mower', aliases:['robotic mowers','robot mowers','automowers'], types:['Robotic Mowers']},
    {key:'chainsaw', aliases:['chainsaws','chainsaw'], types:['Chainsaws']},
    {key:'brushcutter', aliases:['brushcutters','brushcutter','whipper snippers'], types:['Brushcutters']},
    {key:'trimmer', aliases:['hedge trimmers','hedge trimmer','hedgetrimmers'], types:['Hedgetrimmers']},
    {key:'blower', aliases:['leaf blowers','leaf blower','blowers','blower'], types:['Blowers','Blower']},
    {key:'pump', aliases:['water pumps','water pump','pumps','pump'], types:['Pumps']},
    {key:'edger', aliases:['lawn edgers','lawn edger','edgers','edger'], types:['Edgers','Edger']},
    {key:'chipper', aliases:['wood chippers','wood chipper','chippers','chipper'], types:['Chippers']},
    {key:'pressure-washer', aliases:['pressure washers','pressure washer'], types:['Pressure Washer']},
    {key:'sprayer', aliases:['sprayers','sprayer'], types:['Sprayer']},
    {key:'cultivator', aliases:['cultivators','cultivator','tillers','tiller'], types:['Cultivators','Tiller & Cultivator']},
    {key:'post-driver', aliases:['post drivers','post driver'], types:['Post Drivers']},
    {key:'post-hole-digger', aliases:['post hole diggers','post hole digger'], types:['Post Hole Diggers']},
    {key:'power-cutter', aliases:['power cutters','power cutter'], types:['Power Cutters']},
    {key:'compactor', aliases:['compactors','compactor','rammers','rammer'], types:['Compactors','Rammers']},
    {key:'pole-saw', aliases:['pole saws','pole saw'], types:['Polesaw']},
  ];
  const normalize = value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
  const escapeTerm = value => `"${String(value).replace(/"/g, '\\"')}"`;
  const searchPath = '/search';
  const originalKey = 'fgm_original_q';

  function interpret(raw) {
    const helper = window.FGMSearchIntent;
    if (!helper || !raw || /\b(?:tag|title|vendor|product_type)\s*:/i.test(raw) || helper.isPartSearch(raw)) return null;
    const corrected = raw.replace(/\bsacg\b/gi, 'scag');
    const brand = helper.matchingBrands(corrected)[0];
    if (!brand) return null;
    const remainder = normalize(helper.removeBrand(corrected, brand));
    const family = FAMILIES.find(item => item.aliases.includes(remainder));
    if (!family) return null;
    return {original:raw.trim(), brand:brand.name, brandTag:brand.tag, family};
  }

  function machineQuery(intent) {
    const typeTerms = intent.family.types.map(type => `product_type:${escapeTerm(type)}`);
    return ['tag:catalog_machine', typeTerms.length > 1 ? `(${typeTerms.join(' OR ')})` : typeTerms[0], `vendor:${escapeTerm(intent.brand)}`].join(' ');
  }

  function targetUrl(intent) {
    const url = new URL(searchPath, location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', machineQuery(intent));
    url.searchParams.set(originalKey, intent.original);
    url.searchParams.set('fgm_equipment', intent.family.key);
    url.searchParams.set('fgm_brand', intent.brandTag);
    return url;
  }

  function modelOf(title, brand, family) {
    const withoutBrand = title.replace(new RegExp(brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig'), ' ');
    const withoutFamily = withoutBrand.replace(new RegExp(family.aliases.join('|').replace(/ /g, '\\s*'), 'ig'), ' ');
    const tokens = withoutFamily.match(/\b[A-Z]{1,7}[\d][A-Z\d-]{2,}\b/gi) || [];
    const model = tokens.find(token => /[a-z]/i.test(token) && /\d/.test(token) && !/^(?:HP|CC|V|AH|MM)\d/i.test(token));
    if (!model) return null;
    const root = model.replace(/(?:is|i|n)$/i, '');
    return {exact:model.toUpperCase(), root:(root.length >= 4 ? root : model).toUpperCase()};
  }

  function cards(documentRoot) {
    return [...documentRoot.querySelectorAll('#product-grid .product-grid > .grid__item')]
      .map(element => ({element, title:element.querySelector('.card__heading a')?.textContent.trim() || '', href:element.querySelector('.card__heading a')?.getAttribute('href') || ''}))
      .filter(item => item.title && item.href);
  }

  function resultCount(documentRoot) {
    const text = documentRoot.querySelector('#ProductCountDesktop, #ProductCount')?.textContent || '';
    return Number(text.replace(/,/g, '').match(/\d+/)?.[0] || 0);
  }

  async function fetchCards(query) {
    const url = new URL(searchPath, location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', query);
    const collected = [];
    let total = 0;
    for (let page = 1; page <= 25; page++) {
      url.searchParams.set('page', String(page));
      const response = await fetch(url, {credentials:'same-origin'});
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
      const documentRoot = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (page === 1) total = resultCount(documentRoot);
      const batch = cards(documentRoot);
      collected.push(...batch);
      if (!batch.length || collected.length >= total || batch.length < 24) break;
    }
    return collected;
  }

  function titleMatchesModel(title, model) {
    const words = String(title).toUpperCase().match(/[A-Z]+\d+[A-Z\d-]*/g) || [];
    return words.some(word => word === model.exact || word.startsWith(model.exact) || word === model.root);
  }

  function unique(items) {
    const seen = new Set();
    return items.filter(item => {
      const key = item.href.split('?')[0];
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function partQuery(intent, models) {
    const familyTerms = intent.family.aliases.filter(alias => !alias.endsWith('s')).slice(0, 3);
    const terms = [
      ...models.map(model => `title:${escapeTerm(model.exact)}`),
      ...models.filter(model => model.root !== model.exact).map(model => `title:${escapeTerm(model.root)}`),
      ...familyTerms.map(alias => `title:${escapeTerm(alias)}`),
    ];
    return `tag:catalog_parts tag:${intent.brandTag} (${[...new Set(terms)].join(' OR ')})`;
  }

  function makeHeading(label) {
    const heading = document.createElement('h2');
    heading.className = 'h2 fgm-equipment-heading';
    heading.textContent = label;
    return heading;
  }

  function makeGrid(template, list) {
    const grid = template.cloneNode(false);
    grid.replaceChildren(...list.map(item => item.element));
    return grid;
  }

  async function renderResults(params) {
    const family = FAMILIES.find(item => item.key === params.get('fgm_equipment'));
    const brandTag = params.get('fgm_brand');
    const brand = brandTag?.startsWith('collection_brand_') ? brandTag.slice(17).replace(/_/g, ' ') : '';
    const original = params.get(originalKey) || '';
    if (!family || !brand || !original) return;
    const container = document.querySelector('#product-grid');
    if (!container) return;
    const grid = container.querySelector('.product-grid') || Object.assign(document.createElement('ul'), {
      className:'grid product-grid grid--2-col-tablet-down grid--4-col-desktop',
    });
    const nativeCards = cards(document);
    const nativeTotal = resultCount(document);
    const machineCards = [...nativeCards];
    container.classList.add('fgm-equipment-results');
    container.setAttribute('aria-busy', 'true');
    try {
      // The initial Liquid response is page 1 of a strict machine query.
      if (nativeTotal > nativeCards.length) {
        const all = await fetchCards(params.get('q'));
        machineCards.splice(0, machineCards.length, ...all);
      }
      const models = machineCards.map(item => modelOf(item.title, brand, family));
      const confirmed = models.filter(Boolean);
      let partCards = [];
      {
        const intent = {brandTag, family};
        const candidates = await fetchCards(partQuery(intent, confirmed));
        const assigned = new Set();
        for (const model of confirmed) {
          for (const item of candidates) {
            const key = item.href.split('?')[0];
            if (assigned.has(key) || !titleMatchesModel(item.title, model)) continue;
            assigned.add(key);
            partCards.push(item);
          }
        }
        if (!confirmed.length) {
          const aliases = family.aliases.filter(alias => !alias.endsWith('s'));
          partCards = candidates.filter(item => aliases.some(alias => normalize(item.title).includes(alias)));
        }
      }
      partCards = unique(partCards);
      const machineGrid = makeGrid(grid, machineCards);
      const nodes = [makeHeading(`${machineCards.length} matching machines`), machineGrid];
      if (partCards.length) nodes.push(makeHeading(`${partCards.length} ${confirmed.length ? 'model-matched' : 'related'} parts`), makeGrid(grid, partCards));
      container.replaceChildren(...nodes);
      document.querySelectorAll('#ProductCountDesktop, #ProductCount')
        .forEach(count => { count.textContent = `${machineCards.length + partCards.length} results`; });
      document.querySelectorAll('.template-search__header p[role="status"]')
        .forEach(message => { message.textContent = `${machineCards.length + partCards.length} results for ${original}`; });
      const sortControls = document.querySelector('#main-search-filters');
      if (sortControls) sortControls.hidden = true;
      document.title = `Search: ${machineCards.length + partCards.length} results for "${original}" – Ferntreegullymowerpower`;
    } catch (error) {
      console.error('FGM equipment search:', error);
      // Keep the strict machine results visible if the parts request fails.
    } finally {
      container.setAttribute('aria-busy', 'false');
    }
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    let action;
    try { action = new URL(form.action, location.origin); } catch (_) { return; }
    if (action.pathname.replace(/\/$/, '') !== searchPath) return;
    const input = form.querySelector('input[name="q"]:not([type="hidden"])');
    const intent = interpret(input?.value);
    if (!intent) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    location.assign(targetUrl(intent));
  }, true);

  if (location.pathname.replace(/\/$/, '') === searchPath) {
    const params = new URL(location.href).searchParams;
    if (params.has('fgm_equipment')) renderResults(params);
    else {
      const intent = interpret(params.get('q'));
      if (intent) location.replace(targetUrl(intent));
    }
  }
  window.FGMEquipmentSearch = {interpret, machineQuery, modelOf, titleMatchesModel};
})();
